import React from 'react';
import { motion } from 'framer-motion';
import { RefreshCw } from 'lucide-react';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const RefundPolicyPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50/50 to-white">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-pink-100 to-rose-100 py-16"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", delay: 0.2 }}
            className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-full shadow-lg mb-6"
          >
            <RefreshCw className="text-pink-500" size={32} />
          </motion.div>
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" 
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Refund Policy
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-lg text-gray-600 italic"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            We want you to love your purchase
          </motion.p>
        </div>
      </motion.div>

      {/* Content */}
      <motion.div 
        variants={staggerContainer}
        initial="hidden"
        animate="visible"
        className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16"
      >
        <motion.div variants={fadeInUp} className="prose prose-pink max-w-none">
          <motion.section 
            variants={fadeInUp}
            className="mb-8 p-6 bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7-Day Return Policy</h2>
            <p className="text-gray-600 leading-relaxed">
              We want you to be completely satisfied with your purchase from The Pink Edit. If you're not 
              happy with your order for any reason, you may return it within 7 days of delivery for a 
              full refund. Items must be unworn, unwashed, and in their original condition with all tags 
              attached.
            </p>
          </motion.section>

          <motion.section 
            variants={fadeInUp}
            className="mb-8 p-6 bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-4">How to Return</h2>
            <p className="text-gray-600 leading-relaxed">
              To initiate a return, please email us at thepinkedit@yahoo.com with your order number and 
              reason for return. We'll provide you with return instructions and a prepaid shipping label 
              for domestic orders. Once we receive and inspect your return, we'll process your refund 
              within 5-7 business days.
            </p>
          </motion.section>

          <motion.section 
            variants={fadeInUp}
            className="mb-8 p-6 bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Exchanges</h2>
            <p className="text-gray-600 leading-relaxed">
              If you'd like to exchange an item for a different size or color, please contact us and we'll 
              be happy to help. Exchanges are subject to availability. If the item you want is out of stock, 
              we'll process a refund instead.
            </p>
          </motion.section>

          <motion.section 
            variants={fadeInUp}
            className="mb-8 p-6 bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Damaged or Defective Items</h2>
            <p className="text-gray-600 leading-relaxed">
              If you receive a damaged or defective item, please contact us immediately at thepinkedit@yahoo.com 
              with photos of the damage. We'll arrange for a replacement or full refund at no additional 
              cost to you. Your satisfaction is our priority.
            </p>
          </motion.section>

          <motion.section 
            variants={fadeInUp}
            className="mb-8 p-6 bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Non-Returnable Items</h2>
            <p className="text-gray-600 leading-relaxed">
              For hygiene reasons, earrings and intimate apparel are final sale and cannot be returned. 
              Sale items marked as "Final Sale" are also non-returnable. <strong>All Duty Blue branded 
              clothing is final sale and cannot be returned or exchanged.</strong> If you have any questions about 
              whether an item can be returned, please contact us before purchasing.
            </p>
          </motion.section>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default RefundPolicyPage;
